'use client'

import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, BadgeCheck, Clock, ShieldCheck, Play, X } from 'lucide-react'
import SEOHead from '../components/SEOHead'
import ProductCard from '../components/ProductCard'
import { api } from '../context/AuthContext'
import { useWishlist } from '../context/WishlistContext'
import { useAuth } from '../context/AuthContext'
import { useAuthModal } from '../context/AuthModalContext'
import { useCart } from '../context/CartContext'
import { toast } from 'sonner'
import { isLaunchLocked } from '../utils/productLaunch'

const photos = [
  '/tank1.jpeg',
  '/tank5.jpeg',
  '/tank6.jpeg',
  '/tank7.jpeg',
  '/tank8.jpeg',
  '/tank9.jpeg',
  '/tank10.jpeg',
  '/tank11.jpeg',
]

const videos = [
  { src: '/tank2.mp4', poster: '/tank1.jpeg' },
]

const features = [
  {
    title: 'MODEL-SPECIFIC FIT',
    text: 'Shaped around the motorcycle for secure mounting, clean alignment and a finish that looks integrated, not improvised.',
    icon: ShieldCheck,
  },
  {
    title: 'PURPOSEFUL PROTECTION',
    text: 'Helps shield high-contact areas from jacket abrasion, luggage rub, minor scuffs and everyday riding wear.',
    icon: BadgeCheck,
  },
  {
    title: 'TOURING-READY DESIGN',
    text: 'Built for daily commutes, weekend rides and long-distance touring, with practical grip points and durable construction.',
    icon: ArrowRight,
  },
  {
    title: 'SUPPORTED AFTER PURCHASE',
    text: 'Get compatibility guidance, installation assistance and responsive after-sales support throughout your ownership experience.',
    icon: Clock,
  },
]

const TankCover = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist()
  const { refreshCartCount } = useCart()
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [outranProducts, setOutranProducts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchOutranProducts()
  }, [])

  const fetchOutranProducts = async () => {
    try {
      const res = await api.get(`/products?is_outran=true`)
      const allOutranProducts = res.data || []
      
      // Filter Outran Originals to only show accessory products with tank cover subcategory
      const outranTankCovers = allOutranProducts.filter(p => 
        !isLaunchLocked(p) &&
        p.product_type === 'accessory' && 
        (p.spare_category_slug === 'tank-cover' || 
         p.spare_category_slug === 'tank_cover' ||
         p.spare_category_name?.toLowerCase().includes('tank cover') ||
         p.spare_category_name?.toLowerCase().includes('tank-cover'))
      )
      
      setOutranProducts(outranTankCovers)
    } catch (error) {
      console.error('Error fetching outran products:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddToCart = async (product) => {
    if (!isAuthenticated) {
      toast.error("Please login to continue")
      openAuth("login")
      return
    }

    if (isLaunchLocked(product)) {
      toast.error(product.launch_badge || "This product is launching soon")
      return
    }

    if (product.product_type === 'combo' || product.item_type === 'combo') {
      navigate(`/products/${product.id}`)
      return
    }

    // Check if product requires color/size selection
    const hasColors = product.colors && product.colors.length > 0
    const hasSizes = product.sizes && product.sizes.length > 0
    const hasVariantStock = product.variant_stock && product.variant_stock.length > 0
    
    // If product has variants, navigate to product details instead of adding directly
    if (hasColors || hasSizes || hasVariantStock) {
      navigate(`/products/${product.id}`)
      return
    }

    try {
      await api.post("/cart/add", {
        product_id: product.id,
        quantity: 1,
      })

      const variantInfo = [product.color].filter(Boolean).join(', ');
      toast.success(`${product.name}${variantInfo ? ` (${variantInfo})` : ''} added to cart!`)
      refreshCartCount()
    } catch (error) {
      console.error("Add to cart error:", error)
      toast.error(error.response?.data?.detail || "Failed to add to cart")
    }
  }

  const handleWishlistToggle = async (product) => {
    if (!isAuthenticated) {
      toast.error("Please login to continue")
      openAuth("login")
      return
    }

    const wasWishlisted = isInWishlist(product.id)
    const nextAction = wasWishlisted ? removeFromWishlist : addToWishlist
    const success = await nextAction(product.id)
    if (success) {
      toast.success(wasWishlisted ? "Removed from wishlist" : "Added to wishlist")
    } else {
      toast.error("Unable to update wishlist")
    }
  }

  const renderProductCard = (product, index) => {
    return (
      <ProductCard
        key={product.id}
        product={product}
        badge="Outran"
        badgeClass="bg-[#ffcc32] text-black"
        layout="grid"
        showStock={false}
        showBadge={true}
        isWishlisted={isInWishlist(product.id)}
        onOpen={(p) => navigate(`/products/${p.id}`)}
        onAddToCart={handleAddToCart}
        onWishlistToggle={handleWishlistToggle}
        onLaunchBlocked={(p, launch) => toast.error(launch?.badge || p.launch_badge || 'Launching soon')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <SEOHead
        title="OUTRAN Tank Covers | Premium Motorcycle Tank Covers | RRIDE"
        description="Explore OUTRAN motorcycle tank covers at RRIDE Moto Studio. Premium fit, protection and finish for your motorcycle."
        canonical="/tank-cover"
        pathname="/tank-cover"
      />

      {/* ================= COMPACT HEADER ================= */}
      <section className="border-b border-white/10 bg-[#0a0a0a] px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto max-w-7xl">
          <p className="text-[#ffcc32] text-[10px] font-semibold uppercase tracking-[0.3em] mb-2">
            OUTRAN ORIGINALS
          </p>
          <h1 
            className="text-3xl md:text-5xl font-light uppercase tracking-wider text-white mb-2"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            OUTRAN TANK COVERS
          </h1>
          <p className="text-sm text-zinc-400 max-w-xl">
            Premium tank covers for model-specific fit and protection.
          </p>
        </div>
      </section>

      {/* ================= PRODUCT GRID ================= */}
      <section className="border-b border-white/10 bg-black px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6">
            <p className="text-[#ffcc32] text-[10px] font-semibold uppercase tracking-[0.3em] mb-1">
              OUTRAN TANK COVERS
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-zinc-400 text-sm">Loading products...</p>
            </div>
          ) : outranProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
              {outranProducts.map((product, index) => renderProductCard(product, index))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-zinc-400 text-sm">No OUTRAN tank covers available.</p>
            </div>
          )}
        </div>
      </section>

      {/* ================= PHOTO GALLERY ================= */}
      <section className="border-b border-white/10 bg-[#070707] px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6">
            <p className="text-[#ffcc32] text-[10px] font-semibold uppercase tracking-[0.3em] mb-1">
              GALLERY
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.map((photo, index) => (
              <motion.button
                key={photo}
                type="button"
                onClick={() => setSelectedPhoto(photo)}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="overflow-hidden rounded-lg border border-white/10 bg-white aspect-[4/5]"
              >
                <img 
                  src={photo} 
                  alt={`OUTRAN tank cover ${index + 1}`} 
                  className="h-full w-full object-cover transition duration-300 hover:scale-105" 
                />
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ================= VIDEOS ================= */}
      <section className="border-b border-white/10 bg-black px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6">
            <p className="text-[#ffcc32] text-[10px] font-semibold uppercase tracking-[0.3em] mb-1">
              OUTRAN IN MOTION
            </p>
          </div>
          <div className="flex justify-center">
            {videos.map((video, index) => (
              <motion.div
                key={video.src}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="overflow-hidden rounded-lg border border-white/10 bg-[#080808] max-w-4xl w-full"
              >
                <video
                  className="w-full aspect-video object-cover"
                  src={video.src}
                  poster={video.poster}
                  controls
                  muted
                  playsInline
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= COMPACT FEATURES ================= */}
      <section className="border-b border-white/10 bg-[#070707] px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="rounded-lg border border-white/10 bg-black/50 p-4 hover:border-[#ffcc32]/20 transition-all"
                >
                  <Icon className="mb-2 h-5 w-5 text-[#ffcc32]" />
                  <h3 className="text-xs font-bold uppercase text-white mb-1">{feature.title}</h3>
                  <p className="text-[11px] leading-5 text-zinc-400">{feature.text}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="bg-black px-4 py-10 md:px-6 md:py-12">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-[#ffcc32] text-[10px] font-semibold uppercase tracking-[0.3em] mb-3">
            EXPLORE ALL OUTRAN PRODUCTS
          </p>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/products?is_outran=true')}
            className="inline-flex items-center gap-2 rounded-lg bg-[#ffcc32] px-6 py-3 font-bold text-black transition-all hover:bg-[#ffcc32]/90"
          >
            SHOP OUTRAN
            <ArrowRight size={16} />
          </motion.button>
        </div>
      </section>

      {/* ================= IMAGE LIGHTBOX ================= */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" 
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            type="button"
            onClick={() => setSelectedPhoto(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white hover:bg-black/80 transition-all"
            aria-label="Close photo viewer"
          >
            <X size={20} />
          </button>
          <img
            src={selectedPhoto}
            alt="OUTRAN tank cover large preview"
            className="max-h-[88vh] max-w-[94vw] rounded-lg object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}

export default TankCover
